/**
 * Custom Shopify Subscription Widget Logic
 *
 * Instructions:
 * 1. Load this script on your product pages, preferably after the main content.
 * In your theme files, you might include it like this:
 * <script src="{{ 'custom-subscriptions.js' | asset_url }}" defer="defer"></script>
 *
 * This script transforms the default Shopify subscription options into an
 * Amazon-style "Subscribe & Save" interface with a dropdown menu.
 */
document.addEventListener('DOMContentLoaded', () => {
  // Shopify apps can load after the initial DOM is ready.
  // A MutationObserver is the most reliable way to wait for the widget to appear.
  const observer = new MutationObserver((mutations, obs) => {
    const container = document.querySelector('.shopify_subscriptions_app_container');
    if (container) {
      // Once the container is found, initialize our custom logic and stop observing.
      initializeSubscriptionWidget(container);
      obs.disconnect();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

function initializeSubscriptionWidget(container) {
  // Find all the necessary elements within the subscription widget
  const fieldsetWrapper = container.querySelector('.shopify_subscriptions_fieldset > div');
  const oneTimeLabel = container.querySelector('.shopify_subscriptions_app_block_label:has(input[data-radio-type="one_time_purchase"])');
  const subscribeLabel = container.querySelector('.shopify_subscriptions_app_block_label:has(input[data-radio-type="selling_plan"])');

  // Exit if the core elements aren't found
  if (!oneTimeLabel || !subscribeLabel || !fieldsetWrapper) {
    console.warn('Subscription widget elements not found. Custom script will not run.');
    return;
  }

  // --- Modify Price Display to add "/pack" ---
  const oneTimePriceElement = oneTimeLabel.querySelector('.shopify_subscriptions_in_widget_price');
  const subscribePriceElement = subscribeLabel.querySelector('.shopify_subscriptions_in_widget_price');

  if (oneTimePriceElement && !oneTimePriceElement.textContent.includes('/pack')) {
    oneTimePriceElement.textContent += '/pack';
  }
  if (subscribePriceElement && !subscribePriceElement.textContent.includes('/pack')) {
    subscribePriceElement.textContent += '/pack';
  }

  // --- OBSERVER TO HANDLE DYNAMIC PRICE UPDATES ---
  if (subscribePriceElement) {
    const priceObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target.textContent && !mutation.target.textContent.includes('/pack')) {
          mutation.target.textContent += '/pack';
        }
      });
    });
    priceObserver.observe(subscribePriceElement, { childList: true, characterData: true, subtree: true });
  }

  const oneTimeRadio = oneTimeLabel.querySelector('input[data-radio-type="one_time_purchase"]');
  const subscriptionFrequencyList = subscribeLabel.querySelector('.shopify_subscriptions_app_block_label_children');
  const subscriptionRadios = subscriptionFrequencyList.querySelectorAll('input[type="radio"]');

  // --- NEW: Create and Insert Discount Badge ---
  const firstSubRadio = subscriptionRadios.length > 0 ? subscriptionRadios[0] : null;
  if (firstSubRadio && firstSubRadio.getAttribute('aria-label')) {
      const ariaLabel = firstSubRadio.getAttribute('aria-label');
      const percentageMatch = ariaLabel.match(/(\d+)%/); // Extracts the number and % sign (e.g., "5%")
      
      if (percentageMatch && percentageMatch[0]) {
          const mainSubscribeLabel = subscribeLabel.querySelector('.shopify_subscriptions_purchase_option_wrapper > label');
          if (mainSubscribeLabel) {
              const discountBadge = document.createElement('span');
              discountBadge.className = 'subscription-discount-badge';
              discountBadge.textContent = `${percentageMatch[0]} OFF`; // Displays "Save 5%"
              mainSubscribeLabel.appendChild(discountBadge);
          }
      }
  }


  // --- Create a Dropdown to Replace the Radio List ---
  const selectWrapper = document.createElement('div');
  selectWrapper.className = 'delivery-frequency-select-wrapper';

  const selectLabel = document.createElement('label');
  selectLabel.setAttribute('for', 'delivery-frequency-select');
  selectLabel.textContent = 'Deliver every:';

  const selectElement = document.createElement('select');
  selectElement.id = 'delivery-frequency-select';
  selectElement.className = 'delivery-frequency-select';
  
  subscriptionRadios.forEach(radio => {
    const option = document.createElement('option');
    option.value = radio.dataset.sellingPlanId;
    let cleanedText = radio.parentElement.textContent.trim().replace('Deliver every ', '').split(',')[0];
    if (!/^\d/.test(cleanedText)) {
      cleanedText = '1 ' + cleanedText;
    }
    option.textContent = cleanedText;
    selectElement.appendChild(option);
  });

  selectWrapper.appendChild(selectLabel);
  selectWrapper.appendChild(selectElement);
  subscribeLabel.appendChild(selectWrapper);

  // --- Create Terms & Conditions Element ---
  const confirmationWrapper = document.createElement('div');
  confirmationWrapper.className = 'subscription-confirmation-details subscription-element-hidden';

  const termsText = document.createElement('p');
  termsText.className = 'subscription-terms-text';
  termsText.innerHTML = `By selecting Subscribe & Save, you agree to our <a href="/policies/terms-of-service" target="_blank">Terms & Conditions</a> and authorize us to charge your default payment method. Auto-renews, skip or cancel anytime. <a href="/policies/subscription-policy" target="_blank">View subscription policy</a>.`;
  confirmationWrapper.appendChild(termsText);
  fieldsetWrapper.appendChild(confirmationWrapper);

  // --- Event Handlers ---
  const updateUI = () => {
    const isSubscriptionSelected = Array.from(subscriptionRadios).some(radio => radio.checked);
    if (isSubscriptionSelected) {
      oneTimeLabel.classList.remove('selected');
      subscribeLabel.classList.add('selected');
      fieldsetWrapper.classList.add('subscription-selected');
      selectWrapper.style.display = 'block';
      confirmationWrapper.classList.remove('subscription-element-hidden');
    } else {
      subscribeLabel.classList.remove('selected');
      oneTimeLabel.classList.add('selected');
      fieldsetWrapper.classList.remove('subscription-selected');
      selectWrapper.style.display = 'none';
      confirmationWrapper.classList.add('subscription-element-hidden');
    }
  };

  oneTimeLabel.addEventListener('click', () => {
    if (!oneTimeRadio.checked) {
      oneTimeRadio.checked = true;
      oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  subscribeLabel.addEventListener('click', (e) => {
    if (e.target.closest('.delivery-frequency-select-wrapper')) return;
    const isAnySubChecked = Array.from(subscriptionRadios).some(r => r.checked);
    if (!isAnySubChecked) {
      const selectedPlanId = selectElement.value;
      const radioToSelect = [...subscriptionRadios].find(r => r.dataset.sellingPlanId === selectedPlanId);
      if (radioToSelect) {
        radioToSelect.checked = true;
        radioToSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  selectElement.addEventListener('change', () => {
    const selectedPlanId = selectElement.value;
    const radioToSelect = [...subscriptionRadios].find(r => r.dataset.sellingPlanId === selectedPlanId);
    if (radioToSelect && !radioToSelect.checked) {
      radioToSelect.checked = true;
      radioToSelect.dispatchEvent(new Event('change', { bubbles: true })); 
    }
  });
  
  container.querySelectorAll('input[type="radio"]').forEach(radio => {
    radio.addEventListener('change', updateUI);
  });

  // --- Set Initial State ---
  if (subscriptionRadios.length > 0) {
    subscriptionRadios[0].checked = true;
    selectElement.value = subscriptionRadios[0].dataset.sellingPlanId;
    subscriptionRadios[0].dispatchEvent(new Event('change', { bubbles: true }));
  } else {
    oneTimeRadio.checked = true;
    oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

